import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CopyGlyph, TickGlyph } from '../components/CopyGlyphs'

/**
 * How a picture card made a picture: the prompt and the settings, which is
 * what the generate page shows beside each run.
 *
 * Carried on the picture itself rather than looked up from the card, because
 * the card is edited again straight afterwards — its prompt is the next
 * attempt, not this one — and because a picture is copied to other boards
 * where there is no card at all.
 */
export interface MadeWith {
  prompt: string
  /** The model's slug, as it was sent. */
  model: string
  size: string
  quality: string
  /** What the whole run cost, as the generate page shows it. */
  credits?: number
  at: string
  /**
   * An upscaled picture (routes/upscale.ts) rather than a made one: there is
   * no prompt, and the box says what it was upscaled with instead. `face` is
   * null for a model with no face switch.
   */
  upscale?: { face: boolean | null; pixels: string }
  /**
   * Cut from another picture in the crop box (Huanrui, 2026-10-27): no model,
   * no prompt. `from` is the original's name, `size` the crop's pixels and
   * `of` the original's.
   */
  crop?: { from: string; size: string; of: string }
  /**
   * Made larger by Bria Expand from the crop box (2026-10-27): `prompt` is
   * what was asked for round it, if anything; the original's size and the
   * new one.
   */
  expand?: { from: string; before: string; after: string }
  /**
   * The Background or Eraser card (2026-10-27): which of Bria's models, the
   * picture it was made from, its size, and for Eraser how many strokes.
   */
  bria?: { op: 'remove' | 'replace' | 'erase'; from: string; size: string; strokes?: number }
}

/** What the button is handed: the record, with the model's name worked out. */
export type MadeWithFound = MadeWith & { modelLabel: string }

type Look =
  | { state: 'idle' }
  | { state: 'looking' }
  | { state: 'found'; made: MadeWithFound }
  | { state: 'none' }
  | { state: 'failed' }

/** Room between the button and the box, and between the box and the window's edge. */
const GAP = 6
const EDGE = 12
const WIDTH = 300

/**
 * The corner button on a generated picture, and the box it opens.
 *
 * Shown only while the pointer is over the picture, so a board of results
 * stays a board of pictures. The box is drawn in the window rather than on
 * the board: on the board it would be scaled with the zoom, and a prompt at a
 * quarter size is not a thing anybody can read or select.
 *
 * It goes away when the pointer leaves it — or on a click anywhere else, a
 * scroll, or Escape, for when the pointer never went into it at all.
 */
export function MadeWithButton({ describe }: { describe: () => Promise<MadeWithFound | null> }) {
  const button = useRef<HTMLButtonElement | null>(null)
  const box = useRef<HTMLDivElement | null>(null)
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const [look, setLook] = useState<Look>({ state: 'idle' })
  const [copied, setCopied] = useState(false)

  const close = useCallback(() => {
    setAt(null)
    setCopied(false)
  }, [])

  const open = useCallback(() => {
    const spot = button.current?.getBoundingClientRect()
    if (!spot) return
    // Under the button, right edges lined up, and kept inside the window.
    const left = Math.min(
      Math.max(EDGE, spot.right - WIDTH),
      window.innerWidth - WIDTH - EDGE,
    )
    setAt({ top: spot.bottom + GAP, left })
    // Asked once. What a picture was made with does not change afterwards.
    if (look.state === 'found' || look.state === 'looking') return
    setLook({ state: 'looking' })
    describe()
      .then((made) => setLook(made ? { state: 'found', made } : { state: 'none' }))
      .catch((error: unknown) => {
        console.warn('could not look up how a picture was made', error)
        setLook({ state: 'failed' })
      })
  }, [describe, look.state])

  // Anything that moves the board moves the picture out from under the box.
  useEffect(() => {
    if (!at) return
    const away = (event: Event) => {
      const target = event.target as globalThis.Node | null
      if (target && (box.current?.contains(target) || button.current?.contains(target))) return
      close()
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', away, true)
    document.addEventListener('wheel', away, true)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away, true)
      document.removeEventListener('wheel', away, true)
      document.removeEventListener('keydown', key)
    }
  }, [at, close])

  // The box does not fit below? Then it goes above, measured once it is drawn.
  useEffect(() => {
    if (!at || !box.current || !button.current) return
    const height = box.current.getBoundingClientRect().height
    if (at.top + height <= window.innerHeight - EDGE) return
    const spot = button.current.getBoundingClientRect()
    const above = Math.max(EDGE, spot.top - GAP - height)
    if (above !== at.top) setAt({ ...at, top: above })
  }, [at, look])

  /*
    Kept out of the board's hands. `nodrag` and `nopan` stop React Flow from
    reading a press on the button as the start of moving the picture; the
    stopped clicks keep it from selecting the node too. The box is portalled,
    but React still bubbles a portal's events up through the node, so it
    stops them as well.
  */
  const stop = (event: React.SyntheticEvent) => event.stopPropagation()

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`cnode__about nodrag nopan${at ? ' cnode__about--open' : ''}`}
        title="How this was made"
        aria-label="How this was made"
        aria-expanded={Boolean(at)}
        onPointerDown={stop}
        onMouseDown={stop}
        onDoubleClick={stop}
        onClick={(event) => {
          event.stopPropagation()
          if (at) close()
          else open()
        }}
      >
        <Sparkles />
      </button>

      {at
        ? createPortal(
            <div
              ref={box}
              className="madewith"
              style={{ top: at.top, left: at.left, width: WIDTH }}
              role="dialog"
              aria-label="How this was made"
              onMouseLeave={close}
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={stop}
              onDoubleClick={stop}
              onContextMenu={stop}
              onWheel={stop}
            >
              {look.state === 'looking' || look.state === 'idle' ? (
                <p className="madewith__quiet">Looking it up…</p>
              ) : look.state === 'none' ? (
                <p className="madewith__quiet">There is no record of how this one was made.</p>
              ) : look.state === 'failed' ? (
                <p className="madewith__quiet">It could not be looked up just now.</p>
              ) : look.made.crop ? (
                <>
                  <div className="madewith__said">
                    <p className="madewith__prompt">
                      Cropped from {look.made.crop.from}: {look.made.crop.size} of its {look.made.crop.of}. The original is unchanged.
                    </p>
                  </div>
                  <span className="madewith__tags">
                    <em>Crop</em>
                    <em>{look.made.crop.size}</em>
                  </span>
                </>
              ) : look.made.bria ? (
                <>
                  <div className="madewith__said">
                    <p className="madewith__prompt">
                      {look.made.bria.op === 'remove'
                        ? `Background taken away from ${look.made.bria.from} with ${look.made.modelLabel}, at ${look.made.bria.size}. The subject is the original's own pixels.`
                        : look.made.bria.op === 'replace'
                          ? `A new background behind ${look.made.bria.from}, by ${look.made.modelLabel}, at ${look.made.bria.size}. The subject is the original's own pixels.`
                          : `${look.made.bria.strokes ?? 0} painted stroke${look.made.bria.strokes === 1 ? '' : 's'} taken out of ${look.made.bria.from} with ${look.made.modelLabel}, at ${look.made.bria.size}. Everything not painted is the original's own pixels.`}
                    </p>
                  </div>
                  {look.made.bria.op === 'replace' ? (
                    <div className="madewith__said">
                      <p className="madewith__prompt">{look.made.prompt || 'No prompt'}</p>
                      {look.made.prompt ? (
                        <button
                          type="button"
                          className="madewith__copy"
                          title="Copy the prompt"
                          aria-label="Copy the prompt"
                          onClick={() => {
                            void navigator.clipboard
                              .writeText(look.made.prompt)
                              .then(() => {
                                setCopied(true)
                                window.setTimeout(() => setCopied(false), 1200)
                              })
                              .catch((error: unknown) => {
                                console.warn('the browser would not copy the prompt', error)
                              })
                          }}
                        >
                          {copied ? <TickGlyph /> : <CopyGlyph />}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <span className="madewith__tags">
                    <em>{look.made.modelLabel}</em>
                    <em>{look.made.bria.size}</em>
                    {look.made.credits ? <em>{look.made.credits} credits</em> : null}
                  </span>
                </>
              ) : look.made.expand ? (
                <>
                  <div className="madewith__said">
                    <p className="madewith__prompt">
                      Expanded from {look.made.expand.from} with {look.made.modelLabel}: {look.made.expand.before} to{' '}
                      {look.made.expand.after}. The original is kept in it as it was.
                    </p>
                  </div>
                  <div className="madewith__said">
                    <p className="madewith__prompt">{look.made.prompt || 'No prompt'}</p>
                    {look.made.prompt ? (
                      <button
                        type="button"
                        className="madewith__copy"
                        title="Copy the prompt"
                        aria-label="Copy the prompt"
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(look.made.prompt)
                            .then(() => {
                              setCopied(true)
                              window.setTimeout(() => setCopied(false), 1200)
                            })
                            .catch((error: unknown) => {
                              console.warn('the browser would not copy the prompt', error)
                            })
                        }}
                      >
                        {copied ? <TickGlyph /> : <CopyGlyph />}
                      </button>
                    ) : null}
                  </div>
                  <span className="madewith__tags">
                    <em>{look.made.modelLabel}</em>
                    <em>{look.made.expand.after}</em>
                    {look.made.credits ? <em>{look.made.credits} credits</em> : null}
                  </span>
                </>
              ) : (
                look.made.upscale ? (
                <>
                  <div className="madewith__said">
                    <p className="madewith__prompt">
                      Upscaled with {look.made.modelLabel} to {look.made.size}
                      {look.made.upscale.pixels ? ` (${look.made.upscale.pixels})` : ''}. The original is unchanged.
                    </p>
                  </div>
                  <span className="madewith__tags">
                    <em>{look.made.modelLabel}</em>
                    <em>{look.made.size}</em>
                    {look.made.upscale.face === null ? null : (
                      <em>{look.made.upscale.face ? 'Faces improved' : 'Faces as they were'}</em>
                    )}
                    {look.made.credits ? <em>{look.made.credits} credits</em> : null}
                  </span>
                </>
                ) : (
                <>
                  <div className="madewith__said">
                    <p className="madewith__prompt">{look.made.prompt || 'No prompt'}</p>
                    {look.made.prompt ? (
                      <button
                        type="button"
                        className="madewith__copy"
                        title="Copy the prompt"
                        aria-label="Copy the prompt"
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(look.made.prompt)
                            .then(() => {
                              setCopied(true)
                              window.setTimeout(() => setCopied(false), 1200)
                            })
                            .catch((error: unknown) => {
                              console.warn('the browser would not copy the prompt', error)
                            })
                        }}
                      >
                        {copied ? <TickGlyph /> : <CopyGlyph />}
                      </button>
                    ) : null}
                  </div>
                  <span className="madewith__tags">
                    <em>{look.made.modelLabel}</em>
                    <em>{look.made.size || 'auto'}</em>
                    <em>{look.made.quality || 'auto'}</em>
                    {look.made.credits ? <em>{look.made.credits} credits</em> : null}
                  </span>
                </>
                )
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/** Two stars: the usual sign for "a model made this". */
function Sparkles() {
  return (
    <svg viewBox="1.6 1.6 12.8 12.8" width="16" height="16" aria-hidden="true">
      <path
        d="M6.2 2.2c.35 2.5 1.3 3.45 3.8 3.8-2.5.35-3.45 1.3-3.8 3.8-.35-2.5-1.3-3.45-3.8-3.8 2.5-.35 3.45-1.3 3.8-3.8Z"
        fill="currentColor"
      />
      <path
        d="M11.4 8.6c.22 1.55.8 2.13 2.35 2.35-1.55.22-2.13.8-2.35 2.35-.22-1.55-.8-2.13-2.35-2.35 1.55-.22 2.13-.8 2.35-2.35Z"
        fill="currentColor"
      />
    </svg>
  )
}
